import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../../utils/ToastSettings";
import { UsersService } from "../../../services/Access/Users/UsersService";
import { UserModel } from "../../../models/Access/Users/UserModel";
import { number } from "yup";

type StatusTutorialResult = {
  success: boolean;
  passedTutorial: number;
  error: boolean;
  message?: string;
};

interface ContextProps {
  user: UserModel;
  setUser: Dispatch<SetStateAction<UserModel>>;
  users: UserModel[];
  params: any;
  paymentMethod: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  error: string;
  statusTutorial: any;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleReactivate: (id: number) => Promise<boolean>;
  handleStatusTutorial: () => Promise<StatusTutorialResult>;
  handleFinishedTutorial: () => Promise<boolean>;
  handleResetTutorial: () => Promise<boolean>;
  handleListPaymentsMethods: (
    id: number,
  ) => Promise<{ success: boolean; data: any }>;
  handleCompletedToday: () => Promise<boolean>;
  userTypesOptions: any[];
}

export const UsersContext = createContext<ContextProps>({} as ContextProps);

interface UsersProviderProps {
  children: ReactNode;
}

export const UsersProvider = ({ children }: UsersProviderProps) => {
  const [user, setUser] = useState<UserModel>({} as UserModel);
  const [users, setUsers] = useState<UserModel[]>([]);
  const [params, setParams] = useState<any | null>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [statusTutorial, setStatusTutorial] = useState<any | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<any>("");
  const [userTypesOptions, setUserTypesOptions] = useState<any[]>([]);

  const service = new UsersService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/access/users/list")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setUsers(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os usuários do sistema.");
    }
  }, [params, location.pathname]);

  const handleFetch = async (data: UserModel) => {
    try {
      const { access_profile, created_at, updated_at, deleted_at, ...payload } =
        data;

      const [_Response, _Error, _Code, _Errors] = await service.fetch(payload);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar o usuário");
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar o usuário",
      );
      return false;
    }
  };

  const handleReactivate = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.reactivate(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao reativar o usuário",
      );
      return false;
    }
  };

  const handleStatusTutorial = async () => {
    try {
      setIsLoading(true);
      const [response, error] = await service.statusTutorial();
      setIsLoading(true);

      if (error) {
        setError(response?.message || error);
        return {
          success: false,
          passedTutorial: 0,
          error: true,
          message: response?.message || error,
        };
      }

      const passedTutorial = response?.passed_tutorial ?? 0;

      setStatusTutorial(passedTutorial);
      setError("");

      return {
        success: true,
        passedTutorial,
        error: false,
      };
    } catch (e: any) {
      setIsLoading(false);
      const message =
        e.response?.data?.message || "Não foi possível obter status tutorial.";

      setError(message);

      return {
        success: false,
        passedTutorial: 0,
        error: true,
        message,
      };
    }
  };

  const handleFinishedTutorial = async () => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.finishedTutorial();
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setStatusTutorial(
        _Response?.data?.passed_tutorial ? _Response?.data?.passed_tutorial : 0,
      );

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao finalizar o tutorial.",
      );
      return false;
    }
  };

  const handleResetTutorial = async () => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.resetTutorial();
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setStatusTutorial(
        _Response?.data?.passed_tutorial ? _Response?.data?.passed_tutorial : 0,
      );

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao resetar o tutorial.",
      );
      return false;
    }
  };

  const handleListPaymentsMethods = async (id: number) => {
    try {
      setIsLoading(true);

      const [_Response, _Error] = await service.paymentsMethods(id);

      if (_Error) {
        //setError(_Response?.message || _Error);
        return { success: false, data: null };
      }

      setError("");
      setPaymentMethod(_Response?.data);

      return { success: true, data: _Response?.data };
    } catch (e: any) {
      // setError(
      //   e.response?.data?.message || "Houve um erro ao finalizar o tutorial.",
      // );
      return { success: false, data: null };
    }
  };

  const handleCompletedToday = async (): Promise<boolean> => {
    try {
      const [_Response, _Error] = await service.completedToday();
      if (_Error) return false;
      return _Response?.data?.completed_today ?? false;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  useEffect(() => {
    if (!location.pathname.includes("/access/users/list")) return;

    service.types({ withPaginate: false }).then(([_Response, _Error]: any) => {
      if (_Error) return;

      setUserTypesOptions(
        (_Response?.items ?? []).map((item: any) => ({
          label: item.name,
          value: item.id,
        })),
      );
    });
  }, [location.pathname]);

  return (
    <UsersContext.Provider
      value={{
        user,
        setUser,
        users,
        params,
        paymentMethod,
        setParams,
        pages,
        setPages,
        isLoading,
        error,
        statusTutorial,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleReactivate,
        setIsLoading,
        handleStatusTutorial,
        handleFinishedTutorial,
        handleResetTutorial,
        handleListPaymentsMethods,
        handleCompletedToday,
        userTypesOptions,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

export const useUsers = () => useContext(UsersContext);
